import React from 'react';
import styled, { css } from 'styled-components';

interface VariantListProps {
  options: string[];
  selectedOption: string;
  onOptionSelect: (option: string) => void;
  renderOption?: (option: string) => React.ReactNode;
  onHover?: (variantId: string) => void;
  as?: React.ComponentType<any>;
  currentVariantColor?: string;
  optionColor?: (option: string) => void;
  isAvailable?: (option: string) => boolean;
}

export const BaseVariantList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 24px;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const VariantListItem = styled.li<{ isSelected: boolean, $availableToSell?: boolean }>`
  ${(props) =>
    !props.$availableToSell &&
    css`
      opacity: 0.4;
      cursor: not-allowed !important;
      user-select: none;
      &:hover {
        border-color: rgba(0, 0, 0, 0.1) !important;
      }
    `}
  padding: 8px;
  min-width: 80px;
  min-height: 44px;
  border: 1px solid
    ${({ isSelected }) => (isSelected ? 'var(--color-primary)' : 'rgba(0, 0, 0, 0.1)')};
  color: ${({ isSelected }) => (isSelected ? 'var(--color-primary)' : 'inherit')};
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  box-shadow: ${({ isSelected }) => (isSelected ? `0 0 0 3px var(--color-primary-20)` : 'none')};

  &:hover {
    border-color: rgba(0, 0, 0, 0.3);
  }

  span {
    font-weight: 500;
    & + span {
      margin-top: 5px;
    }
  }

  &:last-child {
    padding-right: 8px !important;
  }
`;

const ARIA_LABEL = 'Product variant options';

const VariantList: React.FC<VariantListProps> = ({
  options,
  selectedOption,
  onOptionSelect,
  renderOption,
  onHover,
  as: VariantList = BaseVariantList,
  currentVariantColor,
  optionColor,
  isAvailable,
}) => (
  <VariantList role="listbox" aria-label={ARIA_LABEL}>
    {options.map((option, key) => {
      if (optionColor && optionColor(option) !== currentVariantColor) return null;
      const available = !isAvailable || isAvailable(option);

      return (
        <VariantListItem
          key={key}
          isSelected={selectedOption === option}
          onClick={() => available && onOptionSelect(option)}
          onMouseEnter={() => onHover?.(option)}
          onTouchStart={() => onHover?.(option)}
          role="option"
          aria-selected={selectedOption === option}
          aria-disabled={!available}
          tabIndex={0}
          $availableToSell={available}
          className={available ? '' : 'tmplt-product__variants-item--sold-out'}
        >
          {renderOption ? renderOption(option) : option}
        </VariantListItem>
      );
    })}
  </VariantList>
);

export default VariantList;
